import React, {useContext, useEffect, useState} from 'react';
import _ from "lodash";
import {DocumentDuplicateIcon, XMarkIcon} from "@heroicons/react/24/solid";
import Editor from "../design-editor/editor";
import ShowLink from "../../../common/ui/show-link";
import ShowDesignDetail from "../common/show-design-detail";
import ShowParentProductColorList from "../common/show-parent-product-color-list";
import ShowDesignVariantDetail from "../common/show-design-variant-detail";
import ShowDesignVariantStatus from "../common/show-design-variant-status";
import EditorSettings from "../design-editor/editor-settings";
import DesignArea from "../design-editor/design-area";
import ApplyParentProductColorListBox from "../common/apply-parent-product-color-list-box";
import {getSingleDesignVariant, updateSingleDesignVariant} from "../../services/design-variant-data-service";
import {
    processApplyTemplateStyle,
    processDesignVariantDetail, processImagesLayer,
    processTextItemsLayer
} from "../../utils/design-variant-util";
import {SAVE_DESIGN_TIME_DELAY} from "../../constants/design.constant";
import {processDesignVariantStatistics} from "../../utils/design-util";
import {updateSingleDesign} from "../../services/design-data-service";
import DesignVariantKonvaEditor from "../design-editor/konva-editor/design-variant-konva-editor";
import UpdateDesignVariantStatus from "../common/update-design-variant-status";

const DesignVariantDesigner = ({
                                   selectedVariant,
                                   designDetail,
                                   setDesignDetail,
                                   allTags,
                                   quoteDetail,
                                   parentProduct,
                                   duplicateDesignVariant,
                                   updateVariantColorsInDesign
                               }) => {
    const [showModal, setShowModal] = useState(false);
    const [designVariantDetail, setDesignVariantDetail] = useState(null);
    let [changesCount, setChangesCount] = useState(0);
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedProductColor, setSelectedProductColor] = useState(null);
    const [statusChangeInProgress, setStatusChangeInProgress] = useState(false);
    const [imageUploadInProgress, setImageUploadInProgress] = useState(false);
    const [uploadImage, setUploadImage] = useState(false);

    const getDesignVariantById = () => {
        getSingleDesignVariant(selectedVariant?.id)
            .then(async response => {
                const designVariantDetailData = _.cloneDeep(response.data());
                designVariantDetailData.id = designVariantDetailData.id || selectedVariant?.id;

                processDesignVariantDetail(designVariantDetailData, designDetail, quoteDetail, parentProduct);
                processTextItemsLayer(designVariantDetailData);
                processImagesLayer(designVariantDetailData);

                designVariantDetailData.status = selectedVariant.status;
                setDesignVariantDetail(designVariantDetailData);
            });
    }

    useEffect(() => {
        incrementChangesCount();
    }, [designVariantDetail]);

    useEffect(() => {
        const timeoutId =
            setTimeout(async () => {

                if (changesCount > 0) {
                    resetChangesCount();
                    saveDesignVariantChanges();
                }

            }, SAVE_DESIGN_TIME_DELAY);

        return () => clearTimeout(timeoutId);
    }, [changesCount]);

    const saveDesignVariantChanges = async (existingDesignVariantInput?) => {
        let existingDesignVariant = existingDesignVariantInput;

        if (!existingDesignVariant) {
            existingDesignVariant = {...designVariantDetail};
        }

        if (!_.isEmpty(existingDesignVariant)) {
            // console.log('Before Saving... existingDesignVariant: ', existingDesignVariant);
            await updateSingleDesignVariant(existingDesignVariant);

            if (imageUploadInProgress && uploadImage) {
                await updateVariantInDesign(existingDesignVariant);
                setImageUploadInProgress(false);
                setStatusChangeInProgress(false);
                setUploadImage(false);
            }
        }
    }

    const incrementChangesCount = () => {
        setChangesCount(++changesCount);
    }

    const resetChangesCount = () => {
        setChangesCount(0);
    }

    const markAsReadyToPublish = async () => {
        setStatusChangeInProgress(true);
        setImageUploadInProgress(true);
        setUploadImage(true);
    }

    const markAsInDesign = async () => {
        setStatusChangeInProgress(true);
        setImageUploadInProgress(true);

        setTimeout(async () => {
            const existingDesignVariant = {...designVariantDetail};
            existingDesignVariant.status = 'in-design';

            setDesignVariantDetail(existingDesignVariant);
            resetChangesCount();
            await saveDesignVariantChanges(existingDesignVariant);

            await updateVariantInDesign(existingDesignVariant);
            setStatusChangeInProgress(false);
            setImageUploadInProgress(false);
        }, 100);

    }

    const updateVariantInDesign = async (existingDesignVariant) => {
        const existingDesignDetail = {...designDetail};
        const existingVariants = existingDesignDetail.variants;

        const variantIndex = _.findIndex(existingVariants, ['key', existingDesignVariant.key]);
        existingVariants[variantIndex].status = existingDesignVariant.status;
        existingDesignDetail.variants = existingVariants;
        processDesignVariantStatistics(existingDesignDetail);
        setDesignDetail(existingDesignDetail);

        await updateSingleDesign(existingDesignDetail);
    }

    const applySelectedStyle = async (selectedQuoteTemplate) => {
        const existingDesignVariant = {...designVariantDetail};
        processApplyTemplateStyle(existingDesignVariant, selectedQuoteTemplate);
        setDesignVariantDetail(existingDesignVariant);
        resetChangesCount();
        await saveDesignVariantChanges(existingDesignVariant);
    }

    const removeSelectedProductColorFromVariant = (productColor) => {
        setSelectedProductColor(null);
        const existingDesignVariantDetail = {...designVariantDetail};
        const existingColors = existingDesignVariantDetail.colors || [];

        const colorIndex = _.findIndex(existingColors, ['name', productColor.name]);

        if (colorIndex !== -1) {
            delete existingColors[colorIndex];
        }

        existingDesignVariantDetail.colors = _.orderBy(_.compact(existingColors), 'name');

        setDesignVariantDetail(existingDesignVariantDetail);
        updateVariantColorsInDesign(existingDesignVariantDetail);
    }

    const addSelectedProductColorToVariant = (productColor) => {
        setSelectedProductColor(productColor);
        const existingDesignVariantDetail = {...designVariantDetail};
        const existingColors = existingDesignVariantDetail.colors || [];

        const colorIndex = _.findIndex(existingColors, ['name', productColor.name]);
        const updatedProductColor = _.cloneDeep(productColor);

        if (colorIndex === -1) {
            existingColors.push(updatedProductColor);
        } else {
            existingColors[colorIndex] = updatedProductColor;
        }

        existingDesignVariantDetail.colors = _.orderBy(existingColors, 'name');

        setDesignVariantDetail(existingDesignVariantDetail);
        updateVariantColorsInDesign(existingDesignVariantDetail);
    }

    const init = () => {
        setShowModal(true);
        getDesignVariantById();
    }

    const handleCloseAction = () => {
        setShowModal(false);
    }

    return (
        <>
            <div className="flex items-center justify-center">
                {parentProduct &&
                    <div onClick={init}
                         className="button-outline-small flex w-full justify-center cursor-pointer">
                        <div>Design</div>
                    </div>
                }
            </div>

            {showModal ? (
                <>
                    <div
                        className="justify-center items-center flex overflow-x-hidden overflow-y-auto w-full h-full fixed inset-0 z-50 bg-gray-600 bg-opacity-50">
                        <div className="relative w-full h-full">
                            {/*content*/}
                            <div
                                className="border-0 shadow-lg relative flex flex-col w-full h-full bg-gray-200 outline-none focus:outline-none">
                                {/*header*/}
                                <div className="flex justify-between px-4 pt-4">

                                    <div className="flex justify-start items-start">
                                        <div className="mr-2">
                                            <ShowDesignVariantDetail
                                                designVariantDetail={designVariantDetail}
                                                hideName={true}/>
                                        </div>

                                        {designDetail &&
                                            <div className="">
                                                <ShowDesignDetail designDetail={designDetail}/>
                                            </div>
                                        }
                                    </div>

                                    <div className="ml-2 flex justify-start items-start content-start">
                                        <div><ShowParentProductColorList parentProduct={designVariantDetail}/></div>
                                    </div>

                                    <div className="flex justify-end">
                                        <div className="text-center">
                                            <div className=""><ShowDesignVariantStatus
                                                designVariantDetail={designVariantDetail}/></div>

                                            <UpdateDesignVariantStatus
                                                designVariant={designVariantDetail}
                                                markAsPublished={undefined}
                                                markAsReadyToPublish={markAsReadyToPublish}
                                                markAsInDesign={markAsInDesign}
                                                statusChangeInProgress={statusChangeInProgress && imageUploadInProgress}/>
                                        </div>

                                        <div className="mr-2 ml-4 mt-2">
                                            <button
                                                className="button-gray-outline px-3 py-2"
                                                onClick={handleCloseAction}>
                                                <div className="">
                                                    <XMarkIcon className="h-6 w-6"/>
                                                </div>
                                            </button>
                                        </div>
                                    </div>

                                </div>
                                {/*body*/}
                                <div className="relative px-12 justify-start w-full mt-2">

                                    <div className="flex justify-start items-start">

                                        <div className="w-1/3">
                                            <EditorSettings
                                                allTags={allTags}
                                                parentProduct={parentProduct}
                                                designDetail={designDetail}
                                                designVariantDetail={designVariantDetail}
                                                setDesignVariantDetail={setDesignVariantDetail}
                                                setSelectedImage={setSelectedImage}/>
                                        </div>

                                        <div className="w-2/3">

                                            <div
                                                className="w-full border border-gray-500 ml-2 border border-gray-300 rounded bg-white">
                                                {designVariantDetail?.settings &&
                                                    <DesignVariantKonvaEditor
                                                        updateVariantColorsInDesign={updateVariantColorsInDesign}
                                                        duplicateDesignVariant={duplicateDesignVariant}
                                                        setDesignVariantDetail={setDesignVariantDetail}
                                                        designVariantDetail={designVariantDetail}
                                                        designDetail={designDetail}
                                                        settings={designVariantDetail?.settings}
                                                        currentImage={selectedImage}
                                                        productColor={selectedProductColor}
                                                        uploadImage={uploadImage}
                                                        setUploadImage={setUploadImage}
                                                        setImageUploadInProgress={setImageUploadInProgress}
                                                        changesCount={changesCount}
                                                        applySelectedStyle={applySelectedStyle}
                                                        newImage={selectedImage}/>
                                                }
                                            </div>

                                        </div>

                                        <div
                                            className="w-1/3 ml-8 bg-white border border-gray-300 rounded -mr-4">
                                            <ApplyParentProductColorListBox
                                                parentProduct={parentProduct}
                                                selectedProductColor={selectedProductColor}
                                                setSelectedProductColor={setSelectedProductColor}
                                                selectedColors={designVariantDetail?.colors}
                                                addSelectedProductColorToVariant={addSelectedProductColorToVariant}
                                                removeSelectedProductColorFromVariant={removeSelectedProductColorFromVariant}
                                                containerClass={undefined}/>
                                        </div>

                                    </div>

                                </div>
                                {/*footer*/}
                            </div>
                        </div>
                    </div>
                    <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
                </>
            ) : null}
        </>
    );
}

export default DesignVariantDesigner
